import React, { useState } from 'react';
import {
  Box, Button, TextField, Typography, Paper, Tabs, Tab, Alert, InputAdornment, IconButton, Checkbox, Link
} from '@mui/material';
import WelcomePage from './WelcomePage';
import { auth, db } from './services/firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { setDoc, doc, getDoc, query, collection, getDocs, orderBy, limit } from 'firebase/firestore';
import AdminPage from './AdminPage';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

function generateEAN13() {
  let code = '28';
  while (code.length < 12) {
    code += Math.floor(Math.random() * 10);
  }
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(code[i]) * (i % 2 === 0 ? 1 : 3);
  }
  const checkDigit = (10 - (sum % 10)) % 10;
  return code + checkDigit.toString();
}

function isStrongPassword(password: string) {
  // Almeno 8 caratteri, una maiuscola, una minuscola, un numero e un simbolo
  return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(password);
}

export default function AuthPage() {
  const [tab, setTab] = useState(0);
  const [registered, setRegistered] = useState(false);
  const [user, setUser] = useState<any>(null);

  // Stato per il form di registrazione
  const [registerData, setRegisterData] = useState({
    nome: '',
    cognome: '',
    dataNascita: '',
    cellulare: '',
    email: '',
    password: '',
    confirmPassword: '',
    accettaTermini: false, // Aggiungi questo campo
  });
  const [registerError, setRegisterError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirm, setShowRegConfirm] = useState(false);

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRegisterData({ ...registerData, [e.target.name]: e.target.value });
    setRegisterError('');
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !registerData.nome ||
      !registerData.cognome ||
      !registerData.dataNascita ||
      !registerData.cellulare ||
      !registerData.email ||
      !registerData.password ||
      !registerData.confirmPassword
    ) {
      setRegisterError('Tutti i campi sono obbligatori.');
      return;
    }
    
    // Verifica accettazione termini
    if (!registerData.accettaTermini) {
      setRegisterError('Devi accettare i termini e le condizioni per continuare.');
      return;
    }

    if (!isStrongPassword(registerData.password)) {
      setRegisterError('La password deve contenere almeno 8 caratteri, una lettera maiuscola, una minuscola, un numero e un simbolo.');
      return;
    }
    if (registerData.password !== registerData.confirmPassword) {
      setRegisterError('Le password non coincidono.');
      return;
    }
    try {
      // 1. Trova il massimo codiceCliente esistente
      const utentiRef = collection(db, "utenti");
      const q = query(utentiRef, orderBy("codiceCliente", "desc"), limit(1));
      const snapshot = await getDocs(q);
      let nextCodiceCliente = 1;
      if (!snapshot.empty) {
        const maxDoc = snapshot.docs[0].data();
        nextCodiceCliente = (maxDoc.codiceCliente || 0) + 1;
      }

      // 2. Crea utente su Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        registerData.email,
        registerData.password
      );
      const userId = userCredential.user.uid;

      // 3. Genera barcode
      const barcode = generateEAN13();

      // 4. Salva dati su Firestore, incluso codiceCliente progressivo
      const nuovoUtente = {
        nome: registerData.nome,
        cognome: registerData.cognome,
        dataNascita: registerData.dataNascita,
        cellulare: registerData.cellulare,
        email: registerData.email,
        barcode,
        punti: 0,
        codiceCliente: nextCodiceCliente,
        consensoMarketing: registerData.accettaTermini, // Salva il consenso
        dataConsenso: new Date().toISOString(), // Salva la data del consenso
      };
      await setDoc(doc(db, "utenti", userId), nuovoUtente);

      setUser(nuovoUtente);
      setRegistered(true);
    } catch (err: any) {
      // Controlla specificamente l'errore 'email-already-in-use'
      if (err.code === 'auth/email-already-in-use') {
        setRegisterError('Questa email è già stata registrata. Prova ad accedere.');
      } else {
        // Gestisci altri errori generici
        setRegisterError(err.message || 'Errore durante la registrazione.');
      }
      console.error("Errore registrazione:", err); // Mantieni il log per debug
    }
  };

  // Stato per il form di login
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });
  const [loginError, setLoginError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
    setLoginError('');
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setResetSuccess('');
    if (!loginData.email || !loginData.password) {
      setLoginError('Tutti i campi sono obbligatori.');
      return;
    }
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        loginData.email,
        loginData.password
      );
      const userId = userCredential.user.uid;
      // Recupera dati utente da Firestore
      const docSnap = await getDoc(doc(db, "utenti", userId));
      if (docSnap.exists()) {
        setUser(docSnap.data());
        setRegistered(true);
      } else {
        setLoginError('Utente non trovato.');
      }
    } catch (err: any) {
      setLoginError('Email o password errati.');
    }
  };

  const handlePasswordReset = async () => {
    setLoginError('');
    setResetSuccess('');

    if (!loginData.email) {
      setLoginError('Inserisci la tua email per resettare la password.');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, loginData.email);
      setResetSuccess(`Email di reset inviata a ${loginData.email}. Controlla la tua casella di posta.`);
    } catch (err: any) {
      if (err.code === 'auth/user-not-found') {
        setLoginError('Nessun utente trovato con questa email.');
      } else if (err.code === 'auth/invalid-email') {
        setLoginError('Formato email non valido.');
      } else {
        setLoginError('Errore durante l\'invio dell\'email di reset.');
      }
      console.error("Errore reset password:", err);
    }
  };

  if (registered && user) {
    if (user.email === 'gmmg90@gmail.com') {
      return <AdminPage />;
    }
    return <WelcomePage user={user} />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper elevation={8} sx={{ p: 4, width: '100%', maxWidth: 380, borderRadius: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
        <img src="/logo.png" alt="Logo" style={{ width: 254, height: 64, marginBottom: 8 }} />          <Typography variant="h5" fontWeight={700} color="primary">
            {tab === 0 ? 'Accedi' : 'Registrati'}
          </Typography>
        </Box>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} centered sx={{ mb: 2 }}>
          <Tab label="Login" />
          <Tab label="Registrati" />
        </Tabs>
        {tab === 0 ? (
          <Box component="form" onSubmit={handleLoginSubmit} sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              name="email"
              type="email"
              value={loginData.email}
              onChange={handleLoginChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={loginData.password}
              onChange={handleLoginChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography
              variant="body2"
              align="right"
              sx={{
                mt: 1,
                cursor: 'pointer',
                color: 'primary.main',
                '&:hover': { textDecoration: 'underline' }
              }}
              onClick={handlePasswordReset}
            >
              Password dimenticata?
            </Typography>
            {loginError && <Alert severity="error" sx={{ mt: 1 }}>{loginError}</Alert>}
            {resetSuccess && <Alert severity="success" sx={{ mt: 1 }}>{resetSuccess}</Alert>}
            <Button variant="contained" color="primary" fullWidth type="submit" sx={{ fontWeight: 600, fontSize: 16, mt: 1 }}>
              Accedi
            </Button>
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={handleRegisterSubmit}
            sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <TextField
              label="Nome"
              name="nome"
              value={registerData.nome}
              onChange={handleRegisterChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Cognome"
              name="cognome"
              value={registerData.cognome}
              onChange={handleRegisterChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Data di nascita"
              name="dataNascita"
              type="date"
              value={registerData.dataNascita}
              onChange={handleRegisterChange}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonthIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Cellulare"
              name="cellulare"
              type="tel"
              value={registerData.cellulare}
              onChange={handleRegisterChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIphoneIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={registerData.email}
              onChange={handleRegisterChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Password"
              name="password"
              type={showRegPassword ? 'text' : 'password'}
              value={registerData.password}
              onChange={handleRegisterChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowRegPassword((show) => !show)}
                      edge="end"
                      size="small"
                    >
                      {showRegPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Conferma Password"
              name="confirmPassword"
              type={showRegConfirm ? 'text' : 'password'}
              value={registerData.confirmPassword}
              onChange={handleRegisterChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowRegConfirm((show) => !show)}
                      edge="end"
                      size="small"
                    >
                      {showRegConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mt: 1 }}>
              <Checkbox
                name="accettaTermini"
                checked={registerData.accettaTermini}
                onChange={(e) => setRegisterData({
                  ...registerData,
                  accettaTermini: e.target.checked
                })}
                required
                size="small"
              />
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Accetto i{' '}
                <Link href="/termini-fidelity.pdf" target="_blank" rel="noopener" underline="hover">
                  termini e le condizioni
                </Link>
                {' '}della fidelity card.
              </Typography>
            </Box>
            {registerError && <Alert severity="error">{registerError}</Alert>}
            <Button variant="contained" color="primary" fullWidth type="submit" sx={{ fontWeight: 600, fontSize: 16 }}>
              Registrati
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
